import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router";
import type { CharacterState, HomeSnapshot } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useActiveHome, useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import type { SlotPropsMap } from "@/shared/slots/names";
import { Slot, useSlotContributions } from "@/shared/slots/Slot";
import { ErrorBoundary, Page, QueryView } from "@/shared/ui";
import { ArrivalMoment } from "./ArrivalMoment";
import { HomePlaceLine, RealSiteNotice } from "./RealSiteNotice";
import { AdjustCharacter, usePetCharacter } from "./PetFigure";
import { HomeScene } from "./HomeScene";
import { SceneControls } from "@/shared/ui/SceneControls";
import { preserveSceneState } from "@/shared/ui/sceneNavigation";
import { mapFocusHref } from "@/features/world_map/mapFocus";
import { HomeAlbumFrame } from "./HomeAlbum";
import { useAwayNote } from "./awayNote";
import { useHomeMotionReader } from "./homeMotionReader";
import { useHomeVersionSync } from "./homeVersion";
import "./home.css";

/**
 * 小窝（地图下面的二级页）。旧 UI 已退役（用户 2026-09-24“老的 UI 要退役，不要干扰新的 UI/UX”）：
 * - “生活与回忆”那排入口搬去“回忆”“我的”；管理员“给这个家添伙伴”在“我的 · 我们的家”；
 * - “此刻 · 一起生活”面板与地图首页的主状态面板重复，已去掉：菜熟了看院子里发光的菜园门，
 *   未读看院子信箱探出的信封、屋内左侧信箱小图标上的小红点，TA 出门在外就回地图看；
 * - home.panels 在小窝里一张卡都不放（白名单已清空，见 HOME_PANELS_ALLOWED）。
 */
/**
 * home.panels 在小窝里只放白名单里的卡片。旅途状态卡（journey.status）与刚退役的“此刻 · 一起生活”是同一类旧 UI，
 * 它的去向是地图首页的主状态面板；其他模块往 home.panels 新加的卡也不会自动出现在小窝。
 * 已完成（2026-09-24）：驾校提醒按方案第 8.2 节挪到了地图主状态面板（TA 想学开车、学车进度、领证仪式），
 * 白名单清空，home.panels 在小窝不再渲染任何卡片；以后真要在小窝放卡，先在这里登记理由。
 */
const HOME_PANELS_ALLOWED: ReadonlySet<string> = new Set<string>();

function HomePanels({ snapshot }: { snapshot: HomeSnapshot }) {
  const items = useSlotContributions("home.panels").filter((item) => HOME_PANELS_ALLOWED.has(item.id));
  return (
    <>
      {items.map(({ id, Component }) => {
        // 与 Slot 同样的写法：每张卡各有错误边界，一张出错不拖垮小窝。
        const Typed = Component as unknown as (props: SlotPropsMap["home.panels"]) => ReactNode;
        return (
          <ErrorBoundary key={id} scope={`home.panels:${id}`} compact>
            <Typed snapshot={snapshot} />
          </ErrorBoundary>
        );
      })}
    </>
  );
}

/**
 * “陪 TA 待一会儿”面板里补照片的入口（2531 R26 第 2 条：面板里写着“还没有 TA 的照片”，却没有去补的路）：
 * 和形象页“补一张”入口同一条规矩——live、自己的宠物、还没有照片（领养来的伙伴不上传照片；演示模式不上传）；去 TA 的形象页（/me/look）补。
 */
export function canAddPhotoFromHome(pet: HomeSnapshot["pet"]): boolean {
  return env.dataMode === "live" && pet.origin === "own_pet" && !pet.photo_url;
}

/**
 * 家园快照（每分钟重读）里 TA 的照片和家庭名单里的不一样（没照片的宠物生成了形象照、补了照片，名单还是旧的）：让名单重读一次，
 * 切宠栏、头像跟着换，读屏不再说“暂无照片”（6796 fx0800、I 10-03）。只在两边不一样、并且变了的时候重读一次，不轮询。
 */
export function useListedPhotoFollowsHome(snapshot: HomeSnapshot) {
  const household = useOptionalCurrentHousehold();
  const queryClient = useQueryClient();
  const userId = household?.userId ?? null;
  const inList = household?.pets.find(({ pet }) => pet.pet_id === snapshot.pet.pet_id)?.pet ?? null;
  const listed = inList ? (inList.photo_url ?? null) : undefined;
  const shown = snapshot.pet.photo_url ?? null;
  useEffect(() => {
    if (!userId || listed === undefined || listed === shown) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.households(userId) });
  }, [userId, listed, shown, queryClient]);
}

export function HomeBody({ snapshot, character = null, introPaused = false }: { snapshot: HomeSnapshot; character?: CharacterState | null; introPaused?: boolean }) {
  // TA 出门时门口那张便签写 TA 真实去了哪（和地图同一份状态、同一套说法；读不到就照旧“我出门啦”）
  const awayNote = useAwayNote(snapshot);
  useListedPhotoFollowsHome(snapshot);
  const sceneMotion = useHomeMotionReader(snapshot, character);
  return (
    <div className="ps-home-body">
      <HomeScene
        snapshot={snapshot}
        worldCharacter={character}
        sceneMotion={sceneMotion}
        petActions={
          <>
            {character ? <AdjustCharacter petId={snapshot.pet.pet_id} state={character} /> : null}
            {canAddPhotoFromHome(snapshot.pet) ? (
              <Link className="ps-btn ps-btn--ghost" to="/me/look">
                补一张 {snapshot.pet.name} 的照片
              </Link>
            ) : null}
          </>
        }
        tablePhoto={<HomeAlbumFrame petId={snapshot.pet.pet_id} petName={snapshot.pet.name} />}
        introPaused={introPaused}
        awayNote={awayNote}
        notice={<HomeBulletin snapshot={snapshot} />}
      />
      <div className="ps-home-below">
        <Slot name="home.welcome" props={{ snapshot }} />
        <HomePanels snapshot={snapshot} />
      </div>
    </div>
  );
}

/**
 * 场景底部的告示栏（UI2-REVIEW110-01，I 10-14 夜间实机评审）：关键状态放进首屏。原来「新家地址还在落定」「还在路上」「家在……」三行排在场景下面，
 * 小窝页比视口高（844 的屏要 942–967），这几行都在折叠线下；滚下去以后浮在右边的「看 TA」还会压住食盆。现在和一次性告知同在场景底部这一栏，
 * 挂在 .ps-home-viewport 上贴底（不随镜头平移），压在 TA 走不到的最下面那一截；页面回到一屏高。从上到下：地址落定中、还在路上、一次性告知（不在时是「家在……」那一行）。
 * 只放状态和它们自带的入口（「看看住处」、告知卡的两个按钮），不放说话、出门、菜园这些操作：用户否掉的是底部那条操作横栏
 * （「下面不应该横在那里」，claude-6c2b-home-album-dock 守着 .ps-home-dock 不回来），这里不是它，也不用它的名字。
 */
function HomeBulletin({ snapshot }: { snapshot: HomeSnapshot }) {
  return (
    <div className="ps-home-bulletin" data-testid="home-bulletin">
      {/* 城市定了、地址还在落定（UI2-HOME-SMALL-01）：和出门入口、到家那一刻同一个说法 */}
      {snapshot.place?.address_pending ? <p className="ps-home-catching-up" role="status" data-testid="home-address-pending">新家地址还在落定。落定之前，TA 先待在家里，不出门。</p> : null}
      {snapshot.catching_up ? <p className="ps-home-catching-up" role="status">刚发生的几件小事还在路上：信、工钱和收藏会晚一点到。</p> : null}
      {/* 一次性告知在的时候只放告知（说的是同一件事、点进去同一页）；收起或不该出时放「家在……」那一行。
          新家地址还在落定时不出告知（UI2-POLISH-111-01）：上面那句已经说地址在落定，告知再劝「给 TA 找一个真实的住处」自相矛盾；
          三样叠在一起时 320×640 屋里会顶到「走回庭院」 */}
      {snapshot.place?.address_pending ? <HomePlaceLine snapshot={snapshot} /> : <RealSiteNotice petId={snapshot.pet.pet_id} otherwise={<HomePlaceLine snapshot={snapshot} />} />}
    </div>
  );
}

type ArrivalState = { arrival?: unknown } | null;

/** 到家时刻看过了（主人点了“进家看看”）就在本机记下，之后再带着 arrival 进小窝（例如从等待页回来）不再弹。存储不可用时照旧弹 */
const arrivalSeenKey = (petId: string) => `petsoul:arrival-seen:${petId}`;
function arrivalSeen(petId: string): boolean {
  try {
    return window.localStorage.getItem(arrivalSeenKey(petId)) === "1";
  } catch {
    return false;
  }
}
function rememberArrivalSeen(petId: string) {
  try {
    window.localStorage.setItem(arrivalSeenKey(petId), "1");
  } catch {
    /* 存不了就算了：下次照旧弹，不影响进家 */
  }
}

/** 小窝重试的退避（UI-RESILIENCE-01，I 10-08）：第 1、2、3 次重试前分别等 2、5、10 秒 */
export const HOME_RETRY_DELAYS_MS = [2_000, 5_000, 10_000] as const;
export function homeRetryDelay(failureCount: number): number {
  return HOME_RETRY_DELAYS_MS[Math.min(Math.max(failureCount, 0), HOME_RETRY_DELAYS_MS.length - 1)];
}
/**
 * 正常 60 秒拉一次；上一次失败、手上还留着旧画面时 10 秒再试。
 * 还没有任何数据时照旧 60 秒：react-query 对没有数据的查询重拉时会退回 pending，10 秒一次会让整页在“出错”和“读取中”之间来回闪。
 */
export function homeRefetchInterval(query: { state: { status: string; data?: unknown } }): number {
  return query.state.status === "error" && query.state.data !== undefined ? 10_000 : 60_000;
}

export function HomePage() {
  // UI-RESILIENCE-01：断线不清掉整个家。全局 retry 会再试两次（2 秒、5 秒后）；还不行就保留上一次画面、每 10 秒再试，恢复后回到 60 秒一次（还没有任何数据时照旧 60 秒）
  const query = useActiveHome({ refetchInterval: homeRefetchInterval, retryDelay: homeRetryDelay });
  // HOME-WIRE-02：前台每 10 秒看一眼版本号，TA 出门、到达、回家后十来秒小窝就跟上（不用等上面的 60 秒）
  useHomeVersionSync();
  const currentPetId = useOptionalCurrentHousehold()?.pet?.pet_id ?? null;
  const character = usePetCharacter(query.data?.pet.pet_id);
  const location = useLocation();
  const navigate = useNavigate();
  const arrivalPet = typeof (location.state as ArrivalState)?.arrival === "string" ? ((location.state as ArrivalState)!.arrival as string) : null;
  const [arrivalClosed, setArrivalClosed] = useState(false);
  const closeArrival = () => {
    setArrivalClosed(true);
    if (arrivalPet) rememberArrivalSeen(arrivalPet);
    // 清掉这次跳转带来的状态：返回、刷新都不会再播放到家时刻。
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: preserveSceneState(location.state, "arrival") });
  };
  // 左上角始终能回到地图并对准当前宠物（旧链接带的 ?from=map 照样能用，但不再靠它）。场景出来之前（读取中、出错）也给同一个出口。
  return (
    <Page className="ps-home-page">
      {query.data !== undefined ? null : <SceneControls scene="home" sceneHref={mapFocusHref(currentPetId)} petDisabled />}
      <QueryView query={query} keepData>
        {(snapshot) => {
          const own = character?.pet_id === snapshot.pet.pet_id ? character : null;
          // 入住那一次跳转带来的到家时刻；TA 还在外面时同一处换成“家安好了”那一版（ArrivalMoment 按 presence 分，2026-09-25）。
          // 本机看过一次的不再弹（6796 rc1900 实走：点过“进家看看”再从等待页回来又弹了一次）
          const arriving = Boolean(arrivalPet && !arrivalClosed && arrivalPet === snapshot.pet.pet_id && !arrivalSeen(arrivalPet));
          return (
            <>
              <HomeBody snapshot={snapshot} character={own} introPaused={arriving} />
              {arriving ? <ArrivalMoment snapshot={snapshot} character={own} onClose={closeArrival} /> : null}
            </>
          );
        }}
      </QueryView>
      {/* 在家时小屋仍是主屏（用户 2026-09-27：「在家主屏是小屋、出门是地图」），但不再挂主导航（底栏）：
          用户 10-03「下面不应该横在那里」（HOME-SCENE-REFLOW v2/v3）。通讯器、回忆走场景左侧的小方块（信箱、回忆），地图在左上角 */}
    </Page>
  );
}
